import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { HrailSystem } from "../../../../../http/hrail/types/systems";
import { useHrailSystemsApi } from "../../../../../http/hrail/useSystems";
import { PaginationQuery } from "../../../../../types";
import { formatBoolean } from "../../../../../utils";
import FlexContent from "../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import CreateSystemModalDialogButton from "./CreateSystemModalDialogButton";

type Props = {
  active?: boolean;
};

const Systems: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const { systemId } = useParams<{ systemId: string }>();
  const navigate = useNavigate();

  const navigateToDetailId = (id: string) => navigate(`/admin/systems/${systemId}/hrail_systems/${id}`);
  const navigateToDetail = (system: HrailSystem) => navigateToDetailId(system.id);

  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "hrailSystems" });
  const aborter = useRef(new AbortController());
  const systemApi = useHrailSystemsApi();

  const [itemToDelete, setItemToDelete] = useState<HrailSystem>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "hrailSystems");
  const isActiveTab = active ?? true;
  const updateVisibility = async (system: HrailSystem, visibility: VisibilityScopeValue) => {
    try {
      await systemApi.updateAdminHrailSystemVisibility(system.id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (system: HrailSystem, newOrder: number) => {
    try {
      await systemApi.updateAdminHrailSystemOrder(system.id, { order: newOrder });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const { accessesAction, accessesDialog } = useEntityAccesses<HrailSystem>("hrail.system");

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = systemApi.useAdminHrailSystems(query, aborter.current.signal);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.systems.plural);
    setActions(
      <>
        <SearchBar placeholder={i18n.translation.systems.search} value={searchTerm} onChange={handleSearchChange} />
        <CreateSystemModalDialogButton onCreated={navigateToDetailId} />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<HrailSystem>
        isTall={true}
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.common.code}</th>
            <th align="left">{i18n.translation.common.name}</th>
            <th align="left">{i18n.translation.common.mountingContext}</th>
            <th align="center">{i18n.translation.common.jointTemplate}</th>
            <th align="center">{i18n.translation.common.doubleTerminal}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(system) => (
          <>
            <Table.Td noWrap={true}>
              <Link
                onClick={(event) => event.stopPropagation()}
                to={`/admin/systems/${systemId}/hrail_systems/${system.id}`}
              >
                {system.code ?? ""}
              </Link>
            </Table.Td>
            <Table.Td noWrap={false}>{system.name ?? ""}</Table.Td>
            <Table.Td noWrap={false}>{formatMountingContext(i18n.translation, system.mountingContext)}</Table.Td>
            <Table.Td align="center" noWrap={false}>
              {formatBoolean(i18n.translation, system.isJointHoleTemplateVisible === true)}
            </Table.Td>
            <Table.Td align="center" noWrap={false}>
              {formatBoolean(i18n.translation, system.isDoubleTerminalVisible === true)}
            </Table.Td>
            <td align="center">
              <Visibility value={system.visibility} onChange={(value) => updateVisibility(system, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor order={system.order} onUpdate={(newOrder) => updateOrder(system, newOrder)} />
            </td>
          </>
        )}
        onRowClick={(system) => navigateToDetail(system)}
        rowActions={(system) => [
          getEditAction(i18n, () => navigateToDetail(system)),
          accessesAction(system),
          getDeleteAction(i18n, () => setItemToDelete(system), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}

      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.systems.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                systemApi
                  .deleteAdminHrailSystem(itemToDelete.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.common.deleteEntityQuestion(
            i18n.translation.systems.questions.delete,
            [itemToDelete.code ?? "", itemToDelete.name ?? ""].filter(Boolean).join(" - ") || itemToDelete.id,
          )}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

const formatMountingContext = (translation: ReturnType<typeof useI18n>["translation"], value?: string | null) => {
  switch (value) {
    case "floor":
      return translation.common.floor;
    case "tower":
      return translation.common.tower;
    case "overhead":
      return translation.common.overhead;
    case "wall":
      return translation.common.wall;
    default:
      return value ?? "";
  }
};

export default Systems;
